import {
    APIFacetField,
    APIFinalArgs,
    ArrayQueryResponse,
    BaseFeatureArgs,
    BaseQueryResponse,
    QueryResponse,
    SearchQueryResponse,
} from '@common/types/queryState';
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { isSearchQueryResponse } from '../typeGuards';

type DataMapper<
    TBackend,
    TFrontend,
    TArgs extends APIFinalArgs<BaseFeatureArgs>,
> = (
    response: TBackend[],
    meta: FetchBaseQueryMeta | undefined,
    arg: TArgs,
) => TFrontend[];

interface ExtractedQueryData<TBackend> {
    rawResults: TBackend[];
    totalCount: number;
    facetFields: APIFacetField[];
}

const extractArrayResponse = <TBackend>(
    response: ArrayQueryResponse<TBackend>,
    meta: FetchBaseQueryMeta | undefined,
): ExtractedQueryData<TBackend> => {
    const totalResultsHeader = meta?.response?.headers.get(
        'x-pagination-total-results',
    );
    const totalCount = parseInt(totalResultsHeader ?? '0', 10);
    return {
        rawResults: response,
        totalCount,
        facetFields: [],
    };
};

const extractSearchResponse = <TBackend>(
    response: SearchQueryResponse<TBackend>,
): ExtractedQueryData<TBackend> => {
    return {
        rawResults: response.results,
        totalCount: response.numFound,
        facetFields: response.facetFields || [],
    };
};

const mapFacetKeys = (
    facets: APIFacetField[],
    keyMap?: Record<string, string>,
): APIFacetField[] => {
    if (!keyMap || facets.length === 0) return facets;

    const reverseKeyMap = Object.entries(keyMap).reduce(
        (acc, [feKey, beKey]) => {
            acc[beKey] = feKey;
            return acc;
        },
        {} as Record<string, string>,
    );

    return facets.map((facet) => ({
        ...facet,
        name: reverseKeyMap[facet.name] || facet.name,
    }));
};

export const transformQueryResponse = <
    TBackend,
    TFrontend,
    TArgs extends APIFinalArgs<BaseFeatureArgs>,
>(
    rawResponse: BaseQueryResponse<TBackend>,
    meta: FetchBaseQueryMeta | undefined,
    arg: TArgs,
    dataMapper: DataMapper<TBackend, TFrontend, TArgs>,
): QueryResponse<TFrontend> => {
    const response: ExtractedQueryData<TBackend> = isSearchQueryResponse(
        rawResponse,
    )
        ? extractSearchResponse(rawResponse)
        : extractArrayResponse(rawResponse, meta);

    const data = dataMapper(response.rawResults, meta, arg);

    const facetFields = mapFacetKeys(response.facetFields, arg.keyMap);

    return {
        ...response,
        facetFields,
        data,
    };
};
